import { useState, useEffect, useCallback } from 'react';
import type { CommunityProfile } from './collector.types';

const STORAGE_KEY = 'yodi-active-collector-session';

function loadSession(): CommunityProfile | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function saveSession(profile: CommunityProfile | null): void {
  try {
    if (profile) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {}
}

/**
 * Global persistent hook for the active collector session.
 * The profile is stored in localStorage so it survives page navigations and refreshes.
 * Any contribution made while a session is active should be attributed to this profile.
 */
export function useActiveCollectorSession() {
  const [activeSession, setActiveSessionState] = useState<CommunityProfile | null>(loadSession);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        setActiveSessionState(e.newValue ? JSON.parse(e.newValue) : null);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const setActiveSession = useCallback((profile: CommunityProfile | null) => {
    setActiveSessionState(profile);
    saveSession(profile);
  }, []);

  const clearSession = useCallback(() => {
    setActiveSessionState(null);
    saveSession(null);
  }, []);

  return {
    activeSession,
    setActiveSession,
    clearSession,
    hasActiveSession: activeSession !== null,
  };
}
